/**
 * Business plane provisioning guard — block ERP until ready or intake skipped (P4)
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Spin } from 'antd';
import { useTenant } from '@frontend/contexts/TenantContext';
import {
  buildGenesisIntakeRedirectPath,
  canAccessBusinessPlanePath,
  type BusinessPlaneProvisioningContext,
} from '@shared/tenant-host';
import { buildPlatformUrl } from '@shared/tenant-host/hostContext';
import styles from './BusinessPlaneProvisioningGuard.module.css';

function resolveProvisioningContext(
  tenant: NonNullable<ReturnType<typeof useTenant>['currentTenant']>
): BusinessPlaneProvisioningContext {
  return {
    provisioningStatus: tenant.provisioningStatus ?? null,
    tenantsStatus: tenant.status,
    genesisCompletedAt: tenant.genesisCompletedAt ?? null,
    ...(tenant.intakeSkipped !== undefined ? { intakeSkipped: tenant.intakeSkipped } : {}),
  };
}

export interface BusinessPlaneProvisioningGuardProps {
  children: React.ReactNode;
  tenantRootDomain?: string | null;
}

export const BusinessPlaneProvisioningGuard: React.FC<BusinessPlaneProvisioningGuardProps> = ({
  children,
  tenantRootDomain,
}) => {
  const location = useLocation();
  const { currentTenant, loading } = useTenant();
  const pathname = location.pathname;

  if (loading || !currentTenant) {
    return (
      <div className={styles.spinnerCenter}>
        <Spin size="large" />
      </div>
    );
  }

  const context = resolveProvisioningContext(currentTenant);
  if (canAccessBusinessPlanePath(pathname, context)) {
    return <>{children}</>;
  }

  const rootDomain = tenantRootDomain?.trim();
  if (!rootDomain) {
    return (
      <Result
        status="warning"
        title="조직 설정을 마무리해 주세요"
        subTitle="Genesis Intake를 완료한 뒤 Business plane에 접근할 수 있습니다."
      />
    );
  }

  const intakePath = buildGenesisIntakeRedirectPath(currentTenant.id);
  const platformIntakeUrl = buildPlatformUrl(rootDomain, intakePath);

  if (typeof window !== 'undefined') {
    window.location.replace(platformIntakeUrl);
    return (
      <div className={styles.spinnerCenter}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Result
      status="info"
      title="조직 설정을 마무리해 주세요"
      subTitle="Genesis Intake를 완료하거나 건너뛰기 확인 후 Business plane에 접근할 수 있습니다."
      extra={<Navigate to={intakePath} replace state={{ from: pathname }} />}
    />
  );
};

export default BusinessPlaneProvisioningGuard;
